import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { StratosCommonContent } from "../../../../modules/content/components/StratosCommonContent/StratosCommonContent";
import { StratosMedia } from "../../../../modules/content/components/StratosMedia/StratosMedia";
import { StratosRichText } from "../../../../modules/content/components/StratosRichText/StratosRichText";
import { StratosSection } from "../../../../modules/content/components/StratosSection/StratosSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");

  return (
    <StratosSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <StratosCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-x-6 gap-y-12", columns[columnCount || 3])}>
          {items.map((member, index) => (
            <article className="group flex h-full flex-col" key={index}>
              <StratosMedia
                aspectRatio="3:4"
                className="bg-mist shadow-[0_24px_50px_-38px_rgb(16_23_34/0.6)]"
                imageClassName="transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="relative -mt-8 ml-4 flex flex-1 flex-col rounded-[20px] bg-cloud-white p-5 shadow-[0_18px_40px_-32px_rgb(16_23_34/0.6)] md:ml-6">
                <h3 className="font-stratos-display text-2xl font-bold leading-tight">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-2 font-stratos-body text-sm font-semibold text-primary">
                    {member.role}
                  </p>
                ) : null}
                {member.biography ? (
                  <StratosRichText
                    className="mt-4 text-base text-steel md:text-lg"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-auto flex flex-wrap gap-4 border-t border-horizon-blue pt-3">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="inline-flex min-h-11 items-center font-stratos-body text-xs text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </StratosSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
